import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import {
  handleProblemDetailsError,
  ProblemDetails,
} from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";
import { BulkUpdateCampaignJobBoardInvoiceRequest } from "@/types/request/BulkUpdateCampaignJobBoardInvoiceRequest";
import { CampaignJobBoardInvoiceType } from "@/types/response/CampaignJobBoardInvoiceType";
import { PagedResponse } from "@/types/response/PagedResponse";

type UseBulkUpdateCampaignJobBoardInvoicesParams = {
  jobBoardId: string;
  year: string;
  month: string;
  tenantId?: string | null;
};

/**
 * Updates multiple campaign job board invoices in bulk for a specific job board, year, and month.
 * Automatically invalidates related queries and shows success/error toasts.
 */
export function useBulkUpdateCampaignJobBoardInvoices(
  params: UseBulkUpdateCampaignJobBoardInvoicesParams
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse,
    AxiosError<ProblemDetails>,
    BulkUpdateCampaignJobBoardInvoiceRequest,
    { previousQueries: Array<{ queryKey: readonly unknown[]; data: unknown }> }
  >({
    mutationFn: (request: BulkUpdateCampaignJobBoardInvoiceRequest) =>
      instance
        .put<AxiosResponse>(
          `/api/v2/JobBoards/${params.jobBoardId}/Invoices/${params.year}/${params.month}/Bulk`,
          {
            ...request,
            tenantId: params.tenantId,
          }
        )
        .then((res) => res.data),
    onMutate: async (request) => {
      // Cancel outgoing queries to avoid overwriting optimistic updates
      await queryClient.cancelQueries({
        queryKey: invoiceQueryKeys.jobBoards.details(),
        exact: false,
      });

      // Snapshot previous values for rollback
      const previousQueries: Array<{
        queryKey: readonly unknown[];
        data: unknown;
      }> = [];

      // Capture all affected queries before updating
      const affectedQueries = queryClient
        .getQueryCache()
        .findAll({
          queryKey: invoiceQueryKeys.jobBoards.details(),
          exact: false,
        });

      affectedQueries.forEach((query) => {
        if (query.state.data) {
          previousQueries.push({
            queryKey: query.queryKey,
            data: query.state.data,
          });
        }
      });

      // Optimistically update job board invoice queries (invoiced tab)
      queryClient.setQueriesData(
        {
          queryKey: invoiceQueryKeys.jobBoards.details(),
          exact: false,
        },
        (old: PagedResponse<CampaignJobBoardInvoiceType> | undefined) => {
          if (!old?.data) return old;

          // Update matching invoices optimistically
          const updatedData = old.data.map((item: CampaignJobBoardInvoiceType) => {
            const update = request.invoices.find(
              (inv) =>
                inv.campaignId === item.campaignId &&
                inv.clientId2 === item.clientId2
            );
            if (update) {
              return { ...item, amount: update.amount };
            }
            return item;
          });

          return { ...old, data: updatedData };
        }
      );

      return { previousQueries };
    },
    onError: (error, _variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      handleProblemDetailsError({
        error,
        t,
        showToast,
        fallbackMessage: t("errors.somethingWentWrong"),
      });
    },
    onSuccess: () => {
      // Mark the local query as stale WITHOUT triggering a refetch.
      // The optimistic update from onMutate is already showing the correct value.
      // An immediate refetch would overwrite it before the async event consumer updates the read model.
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.jobBoards.details(),
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.jobBoardCampaigns.byJobBoardYearMonth(
          params.jobBoardId,
          params.year,
          params.month
        ),
        refetchType: "none",
      });
      // Cross-page queries: mark stale, refetchOnMount: true handles refresh on navigation
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.tenantJobBoards.byTenant(params.tenantId ?? null) });
      queryClient.invalidateQueries({ queryKey: ["campaignJobBoards"] });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.campaignJobBoardMonths.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clientBudgetMonth.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clients.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.transactions.all() });

      showToast(t("success.save", { entity: t("common.invoices") }));
    },
  });
}
